import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AuthLayout } from '../components/AuthLayout';
import { Mail, Loader2 } from 'lucide-react';
import { getInitialHref } from '../utils/urlParams';

interface EmailCallbackPageProps {
  onComplete: (needsPassword: boolean, isNewUser: boolean) => void;
}

export const EmailCallbackPage: React.FC<EmailCallbackPageProps> = ({ onComplete }) => {
  const { completeEmailLink, completeEmailLinkWithEmail } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [needsEmail, setNeedsEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const complete = async () => {
      try {
        const result = await completeEmailLink(getInitialHref());
        // Only new users see the post-signup choice screen;
        // existing users go straight through regardless of password status.
        onComplete(result.isNewUser, result.isNewUser);
      } catch (err: any) {
        if (err.message === 'EMAIL_NOT_FOUND') {
          setNeedsEmail(true);
          setLoading(false);
        } else if (err.code === 'auth/invalid-action-code') {
          setError('This link has expired or already been used. Please request a new one.');
          setLoading(false);
        } else {
          setError(err.message || 'Failed to complete sign-in');
          setLoading(false);
        }
      }
    };

    complete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const result = await completeEmailLinkWithEmail(getInitialHref(), email);
      onComplete(result.isNewUser, result.isNewUser);
    } catch (err: any) {
      if (err.code === 'auth/invalid-action-code') {
        setError('This link has expired or already been used. Please request a new one.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter the email address you used to request this link.');
      } else {
        setError(err.message || 'Failed to complete sign-in');
      }
      setSubmitting(false);
    }
  };

  if (loading && !needsEmail) {
    return (
      <AuthLayout title="Completing sign-in..." subtitle="Please wait while we verify your email link.">
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin mx-auto" />
        </div>
      </AuthLayout>
    );
  }

  if (needsEmail) {
    return (
      <AuthLayout title="Confirm your email" subtitle="Enter the email address you used to request this link.">
        <form className="mt-8 space-y-6" onSubmit={handleEmailSubmit}>
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none rounded-md relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your email"
                />
                <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-2.5 z-20 pointer-events-none" />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Verifying...' : 'Continue'}
            </button>
          </div>
        </form>
      </AuthLayout>
    );
  }

  // Error state
  return (
    <AuthLayout title="Something went wrong" iconGradient="from-red-500 to-red-700">
      <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      </div>
    </AuthLayout>
  );
};
